import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { subscriptionPlans } from '../data/subscriptionPlans';
import { TrendingUp, Users, DollarSign, CheckCircle, Clock, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AnalyticsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  
  const currentPlan = subscriptionPlans.find(p => p.id === (user?.subscriptionTier || 'free'));

  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'professional') {
      navigate('/');
    } else {
      fetchQuotes();
    }
  }, [isAuthenticated, user, navigate]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes`);
      setQuotes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setLoading(false);
    }
  };

  // Calculate analytics from real quote data
  const analytics = {
    leadsReceived: quotes.length,
    leadsAccepted: quotes.filter(q => q.status === 'accepted').length,
    leadsRejected: quotes.filter(q => q.status === 'rejected').length,
    leadsPending: quotes.filter(q => q.status === 'pending').length,
    conversionRate: quotes.length > 0 ? Math.round((quotes.filter(q => q.status === 'accepted').length / quotes.length) * 100) : 0,
    avgResponseTime: '2.5 hours',
    profileViews: 156,
    totalSpent: quotes.filter(q => q.status === 'accepted').length * (currentPlan?.leadCost || 24),
    avgLeadCost: currentPlan?.leadCost || 24,
    currentMonth: {
      leads: quotes.filter(q => {
        const quoteDate = new Date(q.createdAt);
        const now = new Date();
        return quoteDate.getMonth() === now.getMonth() && quoteDate.getFullYear() === now.getFullYear();
      }).length,
      spent: quotes.filter(q => {
        const quoteDate = new Date(q.createdAt);
        const now = new Date();
        return q.status === 'accepted' && quoteDate.getMonth() === now.getMonth() && quoteDate.getFullYear() === now.getFullYear();
      }).length * (currentPlan?.leadCost || 24)
    }
  };

  if (!isAuthenticated || user?.userType !== 'professional') {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600 mt-2">Track your performance and lead metrics</p>
            </div>
            <Badge className="bg-orange-600 text-white text-sm py-1 px-3">
              {currentPlan?.name} Plan
            </Badge>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-900">{analytics.leadsReceived}</p>
                  <p className="text-xs text-green-600 mt-1">+{analytics.currentMonth.leads} this month</p>
                </div>
                <Users className="h-10 w-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                  <p className="text-3xl font-bold text-gray-900">{analytics.conversionRate}%</p>
                  <p className="text-xs text-gray-500 mt-1">{analytics.leadsAccepted}/{analytics.leadsReceived} accepted</p>
                </div>
                <TrendingUp className="h-10 w-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Spent</p>
                  <p className="text-3xl font-bold text-gray-900">${analytics.totalSpent}</p>
                  <p className="text-xs text-gray-500 mt-1">${analytics.currentMonth.spent} this month</p>
                </div>
                <DollarSign className="h-10 w-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avg Lead Cost</p>
                  <p className="text-3xl font-bold text-gray-900">${analytics.avgLeadCost}</p>
                  <p className="text-xs text-gray-500 mt-1">Based on {currentPlan?.name} plan</p>
                </div>
                <CreditCard className="h-10 w-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lead Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Lead Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                    <span className="font-medium">Accepted</span>
                  </div>
                  <span className="font-bold text-green-600">{analytics.leadsAccepted}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-yellow-600 mr-3" />
                    <span className="font-medium">Pending</span>
                  </div>
                  <span className="font-bold text-yellow-600">{analytics.leadsPending}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="font-medium">Rejected</span>
                  </div>
                  <span className="font-bold text-red-600">{analytics.leadsRejected}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Average Lead Cost</span>
                  <span className="font-bold text-gray-900">${analytics.avgLeadCost}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Total Leads This Month</span>
                  <span className="font-bold text-gray-900">{analytics.currentMonth.leads}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-gray-600">Spent This Month</span>
                  <span className="font-bold text-gray-900">${analytics.currentMonth.spent}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-600">Subscription Plan</span>
                  <span className="font-bold text-orange-600">{currentPlan?.name}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade CTA for Free Users */}
        {currentPlan?.id === 'free' && (
          <Card className="border-2 border-orange-500 bg-gradient-to-br from-orange-50 to-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Unlock Advanced Analytics
                  </h3>
                  <p className="text-gray-600">
                    Upgrade to Professional or Premium for detailed insights, lower lead costs, and more.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

export default AnalyticsPage;
